import React, { ReactElement } from 'react';

import { Endpoint, ToastService } from '@philips-emr/hpms-provider-commons';
import FilterService from '@philips-emr/hpms-provider-commons/lib/services/filter-service';
import { HpmsCustomCardApi } from '@philips-emr/hpms-provider-components';
import { DeckAPI, DeckValue, Image, Stamp, StampIcon, Token } from '@philips-emr/tws-components';
import { Color, Type } from '@philips-emr/tws-constants';
import Controller, { OnDynamicContentRender, OnMount, OnSelectItem, OnUnmount } from '@philips-emr/tws-core/lib/client';
import { MountEvent, RenderEvent, SelectItemEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService } from '@philips-emr/tws-core/lib/services';

import { MessagesAliases as Aliases, FragmentNames } from '../../codegen';
import { Route } from '../../route';
import BaseController from '../BaseController';

import './messages-controller.css';

enum Status {
  YES = 'S',
  NO = 'N',
}

interface Message {
  nrSeqCommunication: string;
  dsTitle: string;
  dsText: string;
  ieRead: Status;
  ieTypeCommunication: string;
  dtRelease: string;
  ieAttachment: string;
  ieImportant: Status;
  iePriority: string;
  description: string;
  ieNotRead: string;
  qtUnread: string;
}

@Controller({ fragment: FragmentNames.MESSAGES })
class MessagesController extends BaseController {
  @OnMount(Aliases.FILTER_CONSULT_MESSAGES)
  onFilterMount(event: MountEvent): void {
    FilterService.restoreFilterState(event);
  }

  @OnUnmount(Aliases.FILTER_CONSULT_MESSAGES)
  onUnmountFilter(event: MountEvent): void {
    FilterService.persistFilterState(event);
  }

  @OnMount(Aliases.HPMS_CUSTOM_CARD_MESSAGES)
  onMountHpmsCustomCardMessages(event: MountEvent): void {
    const api = event.target as HpmsCustomCardApi;
    const data = api.state;
    const { ieRead } = data as unknown as { ieRead: string };
    if (ieRead !== Status.YES) {
      api.setBackground('hsl(var(--color-5))');
    }
  }

  @OnDynamicContentRender(Aliases.HPMS_DYNAMIC_CONTENT_FIELD_STATUS)
  onDynamicContentRenderHpmsMessageCard(event: RenderEvent): void {
    const { data, render } = event;
    const { ieImportant, ieAttachment } = data as Message;
    const elements: ReactElement[] = [];

    if (ieAttachment === Status.YES) {
      elements.push(<Image alt="" src="/static/assets/DLS_Attachment_32.svg" className="icon-attachment" />);
    }

    if (ieImportant === Status.YES) {
      elements.push(<Stamp icon={StampIcon.WARNING} color={Color.SAFETY_ORANGE} label={''} />);
    }

    render(() => <div className="dynamic-content">
      {...elements}
    </div>);
  }

  @OnDynamicContentRender(Aliases.HPMS_CUSTOM_CARD_MESSAGES)
  onDynamicContentRenderMessages(event: RenderEvent): void {
    const { data, render } = event;
    const { ieRead } = data as Message;
    let notReadToken: ReactElement = <></>;

    if (ieRead !== Status.YES) {
      notReadToken =
        <div className="u-token-bullet">
          <Token type={Type.NOTICE} value={''} />
        </div>;
    }

    render(() => <>
      {notReadToken}
    </>);
  }

  @OnSelectItem(Aliases.DECK_MESSAGES)
  onSelectItemDeckMessages(event: SelectItemEvent<DeckValue>): void {
    const { item, target } = event;
    const api = target as DeckAPI;
    api.unselectAll();

    const { nrSeqCommunication, ieRead } = item as unknown as Message;

    if (ieRead != Status.YES) {
      HttpService.post(Endpoint.POST.MESSAGE_MARK_AS_READ, { nrSequence: nrSeqCommunication })
        .catch((response) => ToastService.open(response));

      const messagesUnreads = this.context.get('messagesUnreads') as Function;
      if (messagesUnreads) {
        messagesUnreads();
      }
    }
    this.historyService.push(Route.MESSAGE_DETAILS, { nrSeqCommunication });
  }

}

export {
  MessagesController,
  Message,
  Status,
};
